import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { arViewerBase, buildArViewerUrl } from '../../../../utils/arViewerUrl';

function buildViewerUrl(sessionId, allowLogin) {
  const base = arViewerBase();
  if (!base || !sessionId) return '';
  return buildArViewerUrl(base, sessionId, allowLogin);
}

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)'
};

// The two-step (scan → sign in → sliders) isn't obvious from the checkbox label
// alone, but it's reassurance rather than instruction — so it hides behind a
// hint marker instead of taking a permanent line in the modal.
const LOGIN_HINT =
  'Off by default — anyone scanning the QR can watch, but not change values.';

const modalPanelStyle = {
  background: '#fff',
  color: '#111',
  borderRadius: 12,
  padding: 24,
  maxWidth: 340,
  textAlign: 'center',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.25)'
};

// Sits directly under the heading rather than beside the QR: the instructor needs to read it
// BEFORE scanning, and it answers a question about the whole modal, not about the code.
const foreignNoteStyle = {
  margin: '0 0 12px',
  padding: '8px 10px',
  borderRadius: 6,
  background: '#f1f3f5',
  fontSize: 13,
  lineHeight: 1.45,
  textAlign: 'left'
};

const hintMarkerStyle = {
  flexShrink: 0,
  width: 18,
  height: 18,
  marginTop: 1,
  padding: 0,
  border: '1px solid rgba(17, 17, 17, 0.35)',
  borderRadius: '50%',
  background: 'transparent',
  color: '#111',
  opacity: 0.65,
  fontSize: 11,
  lineHeight: '16px',
  cursor: 'help'
};

// Stretched across the whole row (left/right 0) rather than centred on the
// marker: the panel's content box is only ~290px wide, so a tooltip sized to
// its own text and centred on a marker at the right edge would hang outside
// the modal. This one cannot overflow whatever the text length.
const hintTooltipStyle = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  right: 0,
  marginBottom: 8,
  padding: '8px 10px',
  borderRadius: 6,
  background: '#1b1f23',
  color: '#fff',
  fontSize: 12,
  lineHeight: 1.45,
  textAlign: 'left',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
  pointerEvents: 'none'
};

const hintCaretStyle = {
  position: 'absolute',
  top: '100%',
  right: 5,
  marginTop: -4,
  width: 8,
  height: 8,
  background: '#1b1f23',
  transform: 'rotate(45deg)'
};

/**
 * Toolbar "AR" button + QR modal. Opening it creates/loads the project's AR session
 * and pushes the current sketch; the QR encodes the viewer URL + session id. Pressing
 * Run (handled in the Toolbar) republishes while this session is active.
 *
 * On a sketch the viewer does not own (an instructor helping a student), the session is a
 * private one of their own and the modal says so — see `ensureArSessionId`.
 */
const ARButton = ({
  isActive,
  sessionId,
  isOpen,
  error,
  busy,
  showGuiInAr,
  foreignOwnerUsername,
  open,
  close,
  stop
}) => {
  const [allowLogin, setAllowLogin] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  // Login on the viewer exists only to edit AR sliders, so it's meaningless
  // unless the sliders are shown in AR. Never append login=1 without showGuiInAr,
  // and drop a stale checked state if the setting is turned off while open.
  useEffect(() => {
    if (!showGuiInAr && allowLogin) setAllowLogin(false);
  }, [showGuiInAr, allowLogin]);
  const url = buildViewerUrl(sessionId, allowLogin && showGuiInAr);
  // Only a press that both starts and ends on the backdrop closes the modal, so a
  // text selection dragged out of the panel doesn't dismiss it.
  const pressStartedOnOverlay = useRef(false);
  const handleOverlayMouseDown = (e) => {
    pressStartedOnOverlay.current = e.target === e.currentTarget;
  };
  const handleOverlayClick = (e) => {
    if (pressStartedOnOverlay.current && e.target === e.currentTarget) close();
    pressStartedOnOverlay.current = false;
  };

  useEffect(() => {
    if (!isOpen) return () => {};
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  return (
    <>
      <button
        className="toolbar__ar-button"
        onClick={() => (isOpen ? close() : open())}
        aria-label="Show this sketch in AR"
        title="Show this sketch in AR on your phone"
        disabled={busy}
      >
        AR
      </button>

      {isOpen && (
        <div
          className="ar-qr-modal"
          style={modalOverlayStyle}
          role="presentation"
          onMouseDown={handleOverlayMouseDown}
          onClick={handleOverlayClick}
        >
          <div
            style={modalPanelStyle}
            role="dialog"
            aria-label="AR session QR code"
          >
            <h3 style={{ margin: '0 0 12px' }}>View in AR</h3>

            {foreignOwnerUsername && (
              <p style={foreignNoteStyle}>
                This is <strong>your own AR session</strong> for{' '}
                <strong>{foreignOwnerUsername}</strong>&apos;s sketch. Their own
                AR session and sliders are not affected.
              </p>
            )}

            {error && (
              <p style={{ color: '#c0392b', margin: '0 0 12px', fontSize: 18 }}>
                {error}
              </p>
            )}

            {url ? (
              <>
                <QRCodeSVG value={url} size={220} includeMargin />
                <p
                  style={{
                    margin: '12px 0 0',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    opacity: 0.7
                  }}
                >
                  {url}
                </p>
              </>
            ) : (
              !error && (
                <p>
                  Set <code>AR_VIEWER_URL</code> in the editor config to
                  generate the QR code.
                </p>
              )
            )}

            {url && (
              <p style={{ margin: '16px 0 0', fontSize: 14 }}>
                Scan with your phone, then press <strong>Run</strong> to push
                your latest code.
              </p>
            )}

            {showGuiInAr && (
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  marginTop: 12,
                  textAlign: 'left'
                }}
              >
                <label htmlFor="ar-allow-login" style={{ fontSize: 13 }}>
                  <input
                    id="ar-allow-login"
                    type="checkbox"
                    checked={allowLogin}
                    onChange={(e) => setAllowLogin(e.target.checked)}
                  />{' '}
                  Let me sign in on my phone to adjust sliders
                </label>
                <button
                  type="button"
                  aria-label={LOGIN_HINT}
                  onMouseEnter={() => setHintOpen(true)}
                  onMouseLeave={() => setHintOpen(false)}
                  onFocus={() => setHintOpen(true)}
                  onBlur={() => setHintOpen(false)}
                  style={hintMarkerStyle}
                >
                  ?
                </button>
                {hintOpen && (
                  <div aria-hidden="true" style={hintTooltipStyle}>
                    {LOGIN_HINT}
                    <span style={hintCaretStyle} />
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                marginTop: 16
              }}
            >
              <button type="button" onClick={close}>
                Close
              </button>
              {isActive && (
                <button type="button" onClick={stop}>
                  Stop AR
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

ARButton.propTypes = {
  isActive: PropTypes.bool.isRequired,
  sessionId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  error: PropTypes.string,
  busy: PropTypes.bool.isRequired,
  showGuiInAr: PropTypes.bool,
  /** Owner's username when the open sketch is someone else's (an instructor viewing a
      student's work); null on your own sketch and on a new, unsaved one. */
  foreignOwnerUsername: PropTypes.string,
  open: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  stop: PropTypes.func.isRequired
};

ARButton.defaultProps = {
  sessionId: null,
  error: null,
  showGuiInAr: false,
  foreignOwnerUsername: null
};

export default ARButton;
